<template>
    <main-container title="Tire Pressure Calculator">
        <div class="notice text-center">
            Intended for metric radial farm tires.
        </div>
        <div class="notice text-center">
            For operation on hillsides with 11% or higher grade, please
            <router-link
                v-if="online"
                :to="{name: 'contact-us'}"
            >
                contact us
            </router-link>
            <span v-if="!online">
                contact us
            </span>
            for recommended air pressure.
        </div>

        <!-- Tire Size -->
        <b-form-group>
            <label
                slot="label"
                for="width"
            >
                Tire Size
                <a
                    v-b-modal.info-tire-size
                    href="#"
                    @click.stop
                ><i class="fa fa-info-circle fa-lg" /></a>
            </label>
            <b-input-group>
                <b-input
                    v-model="width"
                    v-b-tooltip.focus
                    title="Width"
                    class="text-center"
                />
                <b-input
                    value="/"
                    class="col-1 text-center px-0"
                    disabled
                />
                <b-input
                    v-model="aspectRatio"
                    v-b-tooltip.focus
                    title="Aspect Ratio"
                    class="text-center"
                />
                <b-input
                    value="R"
                    class="col-1 text-center px-0"
                    disabled
                />
                <b-input
                    v-model="diameter"
                    v-b-tooltip.focus
                    title="Diameter"
                    class="text-center"
                />
            </b-input-group>
        </b-form-group>

        <!-- Load Index -->
        <b-form-group>
            <label
                slot="label"
                for="load-index"
            >
                Load Index
                <a
                    v-b-modal.info-load-index
                    href="#"
                    @click.stop
                >
                    <i class="fa fa-info-circle fa-lg" />
                </a>
            </label>
            <b-input
                id="load-index"
                v-model="loadIndex"
            />
        </b-form-group>

        <!-- Axle Weight -->
        <b-form-group>
            <label
                slot="label"
                for="axle-weight"
            >
                Axle Weight
                <a
                    v-b-modal.axle-weight-calculator
                    href="#"
                    @click.stop
                >
                    <i class="fa fa-info-circle fa-lg" />
                </a>
            </label>
            <b-input-group>
                <b-input
                    id="axle-weight"
                    v-model="axleWeight"
                />
                <b-button
                    slot="append"
                    v-b-modal.axle-weight-calculator
                    v-b-tooltip.hover
                    class="cursor-pointer"
                    title="Calculate Axle Weight"
                >
                    <i class="fa fa-calculator" />
                </b-button>
            </b-input-group>
        </b-form-group>

        <!-- Tire Type -->
        <b-form-group>
            <label
                slot="label"
                for="type"
            >
                Tire Type
                <a
                    v-b-modal.info-tire-type
                    href="#"
                    @click.stop
                >
                    <i class="fa fa-info-circle fa-lg" />
                </a>
            </label>
            <b-select
                id="type"
                v-model="type"
                :options="typeOptions"
            >
                <option
                    slot="first"
                    :value="null"
                >
                    Please select an option
                </option>
            </b-select>
            <b-alert
                class="my-2"
                :show="isLswException"
            >
                <span v-if="isLswExceptionMarked">
                    For LSW{{ size }}, please use 'R-1, R-1W, R-3' type unless tire is marked with IF or VF designations
                </span>
                <span v-else="">
                    For LSW{{ size }}, please use 'R-1, R-1W, R-3' type."
                </span>
            </b-alert>
        </b-form-group>

        <!-- Application -->
        <b-form-group>
            <label
                slot="label"
                for="application"
            >
                Application
                <a
                    v-b-modal.info-application
                    href="#"
                    @click.stop
                >
                    <i class="fa fa-info-circle fa-lg" />
                </a>
            </label>
            <b-select
                id="application"
                v-model="application"
            >
                <option
                    :value="null"
                    disabled
                >
                    Select Application
                </option>
                <optgroup
                    v-for="app in applicationOptionsFiltered"
                    :key="app.text"
                    :label="app.text"
                >
                    <option
                        v-for="option in app.options"
                        :key="option.value"
                        :value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </optgroup>
            </b-select>
        </b-form-group>

        <!-- Quantity -->
        <b-form-group>
            <label
                slot="label"
                for="quantity"
            >
                Tires
                <a
                    v-b-modal.info-quantity
                    href="#"
                    @click.stop
                >
                    <i class="fa fa-info-circle fa-lg" />
                </a>
            </label>
            <b-select
                id="quantity"
                v-model="quantity"
                :options="quantityOptions"
            />
        </b-form-group>

        <div v-if="canCalculate">
            <router-link
                v-if="hasValidCalculations"
                :to="{name: 'result'}"
                class="btn btn-primary btn-block"
            >
                Calculate
            </router-link>
            <div
                v-if="!hasValidCalculations"
                class="alert alert-warning"
            >
                The provided inputs are invalid.
            </div>
        </div>

        <axle-weight-calculator @calculated="axleWeightCalculated" />
        <info-tire-size />
        <info-tire-type />
        <info-application />
        <info-quantity />
        <info-load-index />
    </main-container>
</template>

<script>
import _ from 'lodash'
import Vuex from 'vuex'

import AxleWeightCalculator from '@/components/axle-weight-calculator'
import InfoApplication from '@/components/info/application'
import InfoLoadIndex from '@/components/info/load-index'
import InfoQuantity from '@/components/info/quantity'
import InfoTireSize from '@/components/info/tire-size'
import InfoTireType from '@/components/info/tire-type'
import MainContainer from '@/components/main-container'
import { typeOptions, quantityOptions } from '@/helpers/options'

const dataProp = (key, fallback) => ({
    get () {
        return _.get(this.data, key, fallback)
    },
    set (value) {
        const data = _.set({}, key, value)
        this.updateData(data)
    },
})

export default {
    name: 'Home',
    components: {
        AxleWeightCalculator,
        InfoApplication,
        InfoQuantity,
        InfoLoadIndex,
        InfoTireSize,
        InfoTireType,
        MainContainer,
    },
    data: () => ({}),
    computed: {
        ...Vuex.mapState(['data', 'online']),
        ...Vuex.mapGetters([
            'isLswException',
            'isLswExceptionMarked',

            'applicationOptionsFiltered',
            'canCalculate',
            'hasValidCalculations',
            'psi',
            'kpa',
            'maxLbs',
            'maxKg',
            'size',
        ]),
        width: dataProp('width'),
        aspectRatio: dataProp('aspectRatio'),
        diameter: dataProp('diameter'),
        loadIndex: dataProp('loadIndex'),
        axleWeight: dataProp('axleWeight'),
        type: dataProp('type'),
        application: dataProp('application'),
        quantity: dataProp('quantity'),
        typeOptions: () => typeOptions,
        quantityOptions: () => quantityOptions,
    },

    methods: {
        ...Vuex.mapMutations(['updateData']),
        axleWeightCalculated (weight) {
            this.axleWeight = weight
        },
    },
}
</script>
